import { useEffect, useState } from "react";
import { getGroupedData } from "../functions/quoteOverviewCalculations";
import QuoteOverviewGroupBys from "./quoteOverviewGroupBys";
import QuoteOverviewFinancialTable from "./quoteOverviewFinancialTable";
import { getQuoteOverviewColumns } from "../functions/quoteOverviewColumns";

const QuoteOverviewFinancial = ({quoteItems, setExportData, isVisible}) => {

    const [groupBySection, setGroupBySection] = useState("quoteGroupDescription");
    const [groupByColumn1, setGroupByColumn1] = useState("supplierName");
    const [groupByColumn2, setGroupByColumn2] = useState("salesforceCode");

    const quoteOverviewColumns = getQuoteOverviewColumns(groupBySection, groupByColumn1, groupByColumn2);

    const contractTableColumns = quoteOverviewColumns.filter(
                    item => item.groupColumn ||
                    item.groupHeader ||
                    item.showOnTables?.includes("contract")
    );

    const annualChargesTableColumns = quoteOverviewColumns.filter(
                    item => item.groupColumn ||
                    item.groupHeader ||
                    item.showOnTables?.includes("annual"))

    const upfrontRecurringTableColumns = quoteOverviewColumns.filter(
        item => item.groupColumn ||
        item.groupHeader ||
        item.showOnTables?.includes("upfront") ||
        item.showOnTables?.includes("recurring")
    );

    const annualChargesTableData = quoteItems.filter(i => i.termLengthMonths > 0)

    const getYears = () => {
        const years = [];
        const maxTermLength = Math.max(...quoteItems.map(i => i.termLengthMonths));
        const maxYears = Math.ceil(maxTermLength / 12);

        for (let year = 1; year <= maxYears; year++) {
            years.push(year);
        }

        return years;
    };

    const convertDataTableToExportFormat = (columns, data) => {
        const rows = []
        const items = data.flatMap(x => x.items);

        rows.push(columns.map(col => ({ value: col.label, type: "text", style: "header", borderBottom: true, align: col.dataType === "currency" ||  col.dataType === "percentage" || col.dataType === "number" ? "right" : "left" })));

        for (const item of items) {

            rows.push(
                columns.map(column => ({
                    value: column.render ? column.render(item[column.key], item, false, false) : item[column.key],
                    dataType: column.dataType || "text"
                }))
            );
        }

        if (items.length > 0) {
            const keysToTotal = Object.keys(items[0]).filter(x => x.startsWith('total'));

            const itemTotals = items.reduce((totals, item) => {
                keysToTotal.forEach(key => {
                    const value = item[key];
                    totals[key] = (totals[key] || 0) + (isFinite(value) ? value : 0);
                });
                return totals;
            }, {});

            rows.push(
                columns.map((column, index) => {
                    const total = column.calculateTotal ? column.calculateTotal(itemTotals) : itemTotals[column.key];
                    if (column.groupHeader || column.groupColumn) {
                        let value = ""

                        if (columns.findLastIndex(x => x.groupHeader || x.groupColumn) === index) {
                            value = "TOTAL:"
                        }

                        return {
                            value: value,
                            dataType: column.dataType || "text",
                            style: "footer",
                            borderTop: true,
                            align: "right"
                        };
                    }

                    return {
                        value: column.render ? column.render(total, itemTotals, false, false) : total,
                        dataType: column.dataType || "text",
                        style: "footer",
                        borderTop: true
                    };
                })
            );
        };

        return rows;
    };

    useEffect(() => {   
        setExportData(prev => {
            const otherSheets = prev?.sheets?.filter(sheet => sheet.id !== "financial") || [];
            let sortOrder = 1;
            const newFinancialSheetData = {
                id: "financial",
                name: "Financial",
                sortOrder: 2,
                tables: []
            }

            newFinancialSheetData.tables.push({
                header: "Contract Totals",
                sortOrder: sortOrder++,
                rows: convertDataTableToExportFormat(contractTableColumns, getGroupedData(quoteItems, groupBySection, groupByColumn1, groupByColumn2)),
            });

            newFinancialSheetData.tables.push({
                header: "Upfront & Recurring Totals",
                sortOrder: sortOrder++,
                rows: convertDataTableToExportFormat(upfrontRecurringTableColumns, getGroupedData(quoteItems, groupBySection, groupByColumn1, groupByColumn2)),
            });

            newFinancialSheetData.tables.push({
                header: "Annual Charges (Per Year)",
                sortOrder: sortOrder++,
                rows: convertDataTableToExportFormat(annualChargesTableColumns, getGroupedData(quoteItems.filter(i => i.termLengthMonths > 0), groupBySection, groupByColumn1, groupByColumn2)),
            });

            getYears().forEach(year => {
                newFinancialSheetData.tables.push({
                    header: `Year ${year}`,
                    sortOrder: sortOrder++,
                    rows: convertDataTableToExportFormat(upfrontRecurringTableColumns, getGroupedData(
                        quoteItems.filter(i => i.recurringStartMonth < year * 12 &&
                            i.termLengthMonths >= (((year - 1) * 12) + 1) || (year === 1 && (i.isUpfront || !i.isRecurring))),
                        groupBySection, groupByColumn1, groupByColumn2, year))
                });
            });

            return (
            {
                ...prev,
                sheets: [
                    ...otherSheets,
                    newFinancialSheetData
                ]
            })

        });
    }, [quoteItems, setExportData, groupBySection, groupByColumn1, groupByColumn2]);

    if (!isVisible) {
        return null;
    }

    return (
        <>
            <QuoteOverviewGroupBys
                id="financial"
                groupBySection={groupBySection}
                groupByColumn1={groupByColumn1}
                groupByColumn2={groupByColumn2}
                setGroupBySection={setGroupBySection}
                setGroupByColumn1={setGroupByColumn1}
                setGroupByColumn2={setGroupByColumn2}
            />
            <QuoteOverviewFinancialTable
                header="Contract Totals"
                quoteItems={getGroupedData(quoteItems, groupBySection, groupByColumn1, groupByColumn2)}
                columns={contractTableColumns}
            />
             <QuoteOverviewFinancialTable
                header="Upfront & Recurring Totals"
                quoteItems={getGroupedData(quoteItems, groupBySection, groupByColumn1, groupByColumn2)}
                columns={upfrontRecurringTableColumns}
            />
            <QuoteOverviewFinancialTable
                header="Annual Charges (Per Year)"
                quoteItems={getGroupedData(annualChargesTableData, groupBySection, groupByColumn1, groupByColumn2)}
                columns={annualChargesTableColumns}
            />
            {
                getYears().map(year => {
                    return(
                        <QuoteOverviewFinancialTable
                            header={`Year ${year}`}
                            quoteItems={getGroupedData(
                                quoteItems.filter(i => i.recurringStartMonth < year * 12 &&
                                    i.termLengthMonths >= (((year - 1) * 12) + 1) || (year === 1 && (i.isUpfront || !i.isRecurring))),
                                    groupBySection, groupByColumn1, groupByColumn2, year)}
                            columns={quoteOverviewColumns.filter(
                                item => item.groupColumn ||
                                item.groupHeader ||
                                item.showOnTables?.includes(year === 1 ? "upfront" : "") ||
                                item.showOnTables?.includes("recurring")
                            )}
                        />
                    )
                })
            }
        </>
    );
}

export default QuoteOverviewFinancial;